/*
  ITAZERO interaction layer
  ---------------------------------------------------------------------------
  INTERACTION NOTE:
  Shopify remains the source of truth. This file only reflects an already
  selected variant in the visible price/button state; the product form still
  submits the real Shopify variant ID.
*/

function initProductGalleries(scope = document) {
  scope.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
    if (gallery.dataset.itazeroReady === "true") return;

    const rail = gallery.querySelector("[data-gallery-thumbs]");
    const thumbs = Array.from(gallery.querySelectorAll("[data-gallery-thumb]"));
    const slides = Array.from(gallery.querySelectorAll("[data-media-id]"));
    const formatIndex = (value) => String(value).padStart(2, "0");

    const normalizeMediaToken = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, "-");

    const colorAliases = {
      black: ["black", "黑", "黑色"],
      white: ["white", "白", "白色"],
      navy: ["navy", "navy-blue", "深蓝", "藏青", "海军蓝", "深海军蓝"],
      blue: ["blue", "蓝", "蓝色"],
      brown: ["brown", "coffee", "咖", "咖色", "棕", "棕色"],
      gray: ["gray", "grey", "灰", "灰色"],
      khaki: ["khaki", "卡其", "卡其色"],
      beige: ["beige", "米", "米色", "象牙白"],
      green: ["green", "绿", "绿色"],
      red: ["red", "红", "红色"],
    };

    const findMediaForOption = (value) => {
      const requested = normalizeMediaToken(value);
      if (!requested) return "";

      const canonical = Object.entries(colorAliases).find(([, aliases]) =>
        aliases.some((alias) => normalizeMediaToken(alias) === requested),
      )?.[0];
      const tokens = canonical ? colorAliases[canonical] : [requested];

      const matchingThumb = thumbs.find((thumb) => {
        const image = thumb.querySelector("img");
        const searchable = normalizeMediaToken(
          [thumb.dataset.mediaAlt, image?.alt, image?.currentSrc, image?.src]
            .filter(Boolean)
            .join(" "),
        );
        return tokens.some((token) =>
          searchable.includes(normalizeMediaToken(token)),
        );
      });

      return matchingThumb?.dataset.mediaTarget || "";
    };

    /* Color-specific media filtering is intentionally deferred.
    const extractColorFromAlt = (alt) => {
      const match = String(alt || "").match(
        /(?:^|[|;,])\s*(?:color|colour|颜色)\s*[:=\-]\s*([^|;,]+)/i,
      );
      return normalizeColor(match?.[1]);
    };
    */

    const updateMediaMetadata = () => {
      const total = thumbs.length;

      thumbs.forEach((thumb, index) => {
        const position = index + 1;
        const label = thumb.querySelector("span");
        if (label) label.textContent = formatIndex(position);
        thumb.setAttribute("aria-posinset", String(position));
        thumb.setAttribute("aria-setsize", String(total));

        const slide = slides.find(
          (candidate) => candidate.dataset.mediaId === thumb.dataset.mediaTarget,
        );
        const current = slide?.querySelector("[data-gallery-counter-current]");
        const count = slide?.querySelector("[data-gallery-counter-total]");
        if (current) current.textContent = formatIndex(position);
        if (count) count.textContent = formatIndex(total);
      });
    };

    const scrollThumbIntoView = (thumb) => {
      if (!rail || !thumb) return;
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      const behavior = reducedMotion ? "auto" : "smooth";

      if (isMobile) {
        rail.scrollTo({
          left: Math.max(
            0,
            thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2,
          ),
          behavior,
        });
      } else {
        rail.scrollTo({
          top: Math.max(
            0,
            thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2,
          ),
          behavior,
        });
      }
    };

    const activateMedia = (mediaId, { scrollThumb = true } = {}) => {
      const mediaKey = String(mediaId || thumbs[0]?.dataset.mediaTarget || "");
      if (!mediaKey) return;

      slides.forEach((slide) => {
        const isActive = slide.dataset.mediaId === mediaKey;
        slide.hidden = !isActive;
        slide.classList.toggle("is-active", isActive);

        if (!isActive) {
          slide.querySelectorAll("video").forEach((video) => video.pause());
        }
      });

      let activeThumb;
      thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.mediaTarget === mediaKey;
        thumb.classList.toggle("is-active", isActive);
        thumb.setAttribute("aria-selected", String(isActive));
        thumb.tabIndex = isActive ? 0 : -1;
        if (isActive) activeThumb = thumb;
      });

      if (scrollThumb) scrollThumbIntoView(activeThumb);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        activateMedia(thumb.dataset.mediaTarget);
      });

      thumb.addEventListener("keydown", (event) => {
        if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)) {
          return;
        }

        event.preventDefault();
        const currentIndex = thumbs.indexOf(thumb);
        const direction = ["ArrowDown", "ArrowRight"].includes(event.key) ? 1 : -1;
        const nextIndex =
          (currentIndex + direction + thumbs.length) % thumbs.length;
        thumbs[nextIndex]?.focus();
        thumbs[nextIndex]?.click();
      });
    });

    const selectedThumb = thumbs.find(
      (thumb) => thumb.getAttribute("aria-selected") === "true",
    );
    updateMediaMetadata();
    activateMedia(selectedThumb?.dataset.mediaTarget || slides[0]?.dataset.mediaId, {
      scrollThumb: false,
    });
    gallery.activateMedia = activateMedia;
    gallery.findMediaForOption = findMediaForOption;
    gallery.dataset.itazeroReady = "true";
  });
}

function initVariantForms(scope = document) {
  scope.querySelectorAll(".product-form").forEach((form) => {
    if (form.dataset.itazeroReady === "true") return;

    const select = form.querySelector("[data-variant-select]");
    const button = form.querySelector("[data-add-to-cart]");
    const optionGroups = Array.from(
      form.querySelectorAll("[data-product-option-group]"),
    ).sort(
      (a, b) =>
        Number(a.dataset.optionIndex || 0) -
        Number(b.dataset.optionIndex || 0),
    );
    const priceContainer = form
      .closest(".product-info")
      ?.querySelector("[data-product-price]");
    const productRoot = form.closest("[data-product-root]");
    const gallery = productRoot?.querySelector("[data-product-gallery]");
    const skuContainer = productRoot?.querySelector("[data-product-sku]");
    const availabilityContainer = productRoot?.querySelector(
      "[data-product-availability]",
    );

    if (!button) return;

    const syncOptionButtons = (option) => {
      if (!option) return;

      optionGroups.forEach((group, groupIndex) => {
        const value =
          option.getAttribute(`data-option-${groupIndex + 1}`) || "";
        if (!value) return;

        group.dataset.selectedValue = value;
        const label = group.querySelector("[data-option-selection]");
        if (label) label.textContent = value;

        group.querySelectorAll("[data-product-option]").forEach((choice) => {
          const isSelected = choice.dataset.optionValue === value;
          choice.classList.toggle("is-selected", isSelected);
          choice.setAttribute("aria-pressed", String(isSelected));
        });
      });
    };

    const syncChoiceAvailability = () => {
      if (!select) return;
      const variants = Array.from(select.options);

      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll("[data-product-option]").forEach((choice) => {
          const proposedValue = choice.dataset.optionValue || "";
          const matchingVariants = variants.filter((candidate) =>
            optionGroups.every((optionGroup, optionIndex) => {
              const expectedValue =
                optionIndex === groupIndex
                  ? proposedValue
                  : optionGroup.dataset.selectedValue || "";
              return (
                candidate.getAttribute(`data-option-${optionIndex + 1}`) ===
                expectedValue
              );
            }),
          );

          const combinationExists = matchingVariants.length > 0;
          const combinationAvailable = matchingVariants.some(
            (candidate) => candidate.dataset.available === "true",
          );
          choice.disabled = !combinationExists;
          choice.classList.toggle(
            "is-sold-out",
            combinationExists && !combinationAvailable,
          );
        });
      });
    };

    const updateVariantState = ({
      updateHistory = false,
      revealMedia = false,
    } = {}) => {
      const option = select.selectedOptions[0];
      const isAvailable = option?.dataset.available === "true";
      const buttonText = button.querySelector("[data-add-to-cart-text]");
      const buttonLabel = isAvailable
        ? button.dataset.addLabel
        : button.dataset.soldLabel;

      button.disabled = !isAvailable;
      if (buttonText) {
        buttonText.textContent = buttonLabel;
      } else if (button.firstChild) {
        button.firstChild.textContent = ` ${buttonLabel} `;
      }

      if (!option) return;
      syncOptionButtons(option);
      syncChoiceAvailability();

      if (skuContainer) {
        skuContainer.textContent = option.dataset.sku || "—";
      }

      if (availabilityContainer) {
        availabilityContainer.textContent = isAvailable ? "AVAILABLE" : "SOLD OUT";
      }

      if (priceContainer) {
        const price = document.createElement("div");
        price.className = option.dataset.comparePrice
          ? "price price--sale"
          : "price";

        if (option.dataset.comparePrice) {
          const compare = document.createElement("s");
          compare.textContent = option.dataset.comparePrice;
          price.append(compare);
        }

        const current = document.createElement("span");
        current.textContent = option.dataset.price || "";
        price.append(current);
        priceContainer.replaceChildren(price);
      }

      if (updateHistory && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", option.value);
        window.history.replaceState({}, "", url);
      }

      let mediaId = option.dataset.featuredMediaId;
      if (!mediaId) {
        const colorGroup = optionGroups.find(
          (group) => group.dataset.optionType === "color",
        );
        mediaId = gallery?.findMediaForOption?.(
          colorGroup?.dataset.selectedValue,
        );
      }
      if (revealMedia && mediaId) {
        if (typeof gallery?.activateMedia === "function") {
          gallery.activateMedia(mediaId);
        } else {
          const media = productRoot?.querySelector(
            `[data-media-id="${CSS.escape(mediaId)}"]`,
          );
          media?.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches
              ? "auto"
              : "smooth",
            block: "nearest",
            inline: "start",
          });
        }
      }
    };

    if (select) {
      select.addEventListener("change", () => updateVariantState());

      optionGroups.forEach((group) => {
        group.querySelectorAll("[data-product-option]").forEach((choice) => {
          choice.addEventListener("click", () => {
            const groupIndex = optionGroups.indexOf(group);
            const proposedValue = choice.dataset.optionValue || "";
            const selections = optionGroups.map(
              (optionGroup, optionIndex) =>
                optionIndex === groupIndex
                  ? proposedValue
                  : optionGroup.dataset.selectedValue || "",
            );
            const matchingVariant = Array.from(select.options).find(
              (candidate) =>
                selections.every(
                  (value, index) =>
                    candidate.getAttribute(`data-option-${index + 1}`) ===
                    value,
                ),
            );

            if (!matchingVariant) return;
            group.dataset.selectedValue = proposedValue;
            select.value = matchingVariant.value;
            updateVariantState({
              updateHistory: true,
              revealMedia: true,
            });
          });
        });
      });

      form.dataset.variantEnhanced = "true";
      updateVariantState();
    }

    form.dataset.itazeroReady = "true";
  });
}

function initQuantityControls(scope = document) {
  scope.querySelectorAll("[data-quantity-input]").forEach((input) => {
    if (input.dataset.itazeroReady === "true") return;

    const container = input.closest(".ita-product-quantity__control");
    container?.querySelectorAll("[data-quantity-change]").forEach((button) => {
      button.addEventListener("click", () => {
        const minimum = Number(input.min || 1);
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const current = Number(input.value || minimum);
        const change = Number(button.dataset.quantityChange || 0);
        input.value = String(
          Math.min(maximum, Math.max(minimum, current + change)),
        );
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    input.dataset.itazeroReady = "true";
  });
}

function initCollectionSorting(scope = document) {
  scope.querySelectorAll("[data-sort-by]").forEach((select) => {
    if (select.dataset.itazeroReady === "true") return;
    select.addEventListener("change", () => select.form?.submit());
    select.dataset.itazeroReady = "true";
  });
}

function initCollectionFilters(scope = document) {
  scope.querySelectorAll("[data-collection-filters]").forEach((details) => {
    if (details.dataset.itazeroReady === "true") return;

    const panel = details.querySelector(".ita-catalogue__filter-panel");
    const summary = details.querySelector("summary");
    const closeButtons = details.querySelectorAll("[data-filter-close]");

    const closeFilters = ({ restoreFocus = true } = {}) => {
      details.removeAttribute("open");
      document.documentElement.classList.remove("ita-filters-open");
      if (restoreFocus) summary?.focus();
    };

    closeButtons.forEach((button) => {
      button.addEventListener("click", () => closeFilters());
    });

    details.addEventListener("toggle", () => {
      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      document.documentElement.classList.toggle(
        "ita-filters-open",
        details.open && isMobile,
      );

      if (details.open && isMobile) {
        window.requestAnimationFrame(() =>
          panel?.querySelector("[data-filter-close]")?.focus(),
        );
      }
    });

    details.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeFilters();
        return;
      }

      if (event.key !== "Tab" || !details.open || !panel) return;
      const focusable = Array.from(
        panel.querySelectorAll(
          "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled])",
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    details.dataset.itazeroReady = "true";
  });
}

function initMobileMenu(scope = document) {
  scope.querySelectorAll("[data-mobile-menu]").forEach((drawer) => {
    if (drawer.dataset.itazeroReady === "true") return;

    drawer.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => drawer.removeAttribute("open"));
    });

    drawer.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        drawer.removeAttribute("open");
        drawer.querySelector("summary")?.focus();
      }
    });

    drawer.dataset.itazeroReady = "true";
  });
}

function initImageMotion(scope = document) {
  const supportsHover = window.matchMedia(
    "(hover: hover) and (pointer: fine)",
  ).matches;
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (!supportsHover || prefersReducedMotion) return;

  if (document.documentElement.dataset.itazeroMotionObserver !== "true") {
    const clearInactiveFrames = (event) => {
      document
        .querySelectorAll(".ita-motion-frame.is-pointer-active")
        .forEach((activeFrame) => {
          if (activeFrame.contains(event.target)) return;
          const activeImage = activeFrame.querySelector(".ita-motion-image");
          activeImage?.style.setProperty("--ita-motion-x", "0px");
          activeImage?.style.setProperty("--ita-motion-y", "0px");
          activeImage?.style.setProperty("--ita-motion-rotate-x", "0deg");
          activeImage?.style.setProperty("--ita-motion-rotate-y", "0deg");
          activeFrame.classList.remove("is-pointer-active");
        });
    };

    document.addEventListener("pointermove", clearInactiveFrames, {
      passive: true,
    });
    document.addEventListener("mousemove", clearInactiveFrames, {
      passive: true,
    });
    document.documentElement.dataset.itazeroMotionObserver = "true";
  }

  scope.querySelectorAll("main img, #MainContent img").forEach((image) => {
    if (image.dataset.itazeroMotionReady === "true") return;
    if (
      image.closest(
        ".manifesto__mark, .site-footer, .product-info, .ita-product-panel, .ita-product-gallery",
      )
    ) {
      return;
    }

    const frame =
      image.closest(
        "figure, a, [class*='__media'], [class*='__visual']",
      ) || image.parentElement;

    if (!frame) return;

    let animationFrame = 0;

    const setMotion = (event) => {
      const bounds = frame.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      frame.classList.add("is-pointer-active");

      const horizontal = Math.max(
        -0.5,
        Math.min(0.5, (event.clientX - bounds.left) / bounds.width - 0.5),
      );
      const vertical = Math.max(
        -0.5,
        Math.min(0.5, (event.clientY - bounds.top) / bounds.height - 0.5),
      );

      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        image.style.setProperty("--ita-motion-x", `${horizontal * 9}px`);
        image.style.setProperty("--ita-motion-y", `${vertical * 7}px`);
        image.style.setProperty(
          "--ita-motion-rotate-x",
          `${vertical * -2.2}deg`,
        );
        image.style.setProperty(
          "--ita-motion-rotate-y",
          `${horizontal * 2.6}deg`,
        );
      });
    };

    const resetMotion = () => {
      window.cancelAnimationFrame(animationFrame);
      image.style.setProperty("--ita-motion-x", "0px");
      image.style.setProperty("--ita-motion-y", "0px");
      image.style.setProperty("--ita-motion-rotate-x", "0deg");
      image.style.setProperty("--ita-motion-rotate-y", "0deg");
      frame.classList.remove("is-pointer-active");
    };

    frame.classList.add("ita-motion-frame");
    image.classList.add("ita-motion-image");
    frame.addEventListener("pointerenter", (event) => {
      frame.classList.add("is-pointer-active");
      setMotion(event);
    });
    frame.addEventListener("pointermove", setMotion, { passive: true });
    frame.addEventListener("pointerleave", resetMotion);
    frame.addEventListener("pointercancel", resetMotion);
    frame.addEventListener("mouseleave", resetMotion);
    frame.addEventListener("mouseout", (event) => {
      if (!frame.contains(event.relatedTarget)) resetMotion();
    });
    image.dataset.itazeroMotionReady = "true";
  });
}

function initItazeroTheme(scope = document) {
  initProductGalleries(scope);
  initVariantForms(scope);
  initQuantityControls(scope);
  initCollectionSorting(scope);
  initCollectionFilters(scope);
  initMobileMenu(scope);
  initImageMotion(scope);
}

document.addEventListener("DOMContentLoaded", () => initItazeroTheme());

/*
  Shopify's Theme Editor can re-render a section without reloading the page.
  Re-initialize only inside the inserted section and guard against duplicates.
*/
document.addEventListener("shopify:section:load", (event) => {
  initItazeroTheme(event.target);
});

